/**
 * The real provider behind the three seams `components/auth/context.ts` defines, replacing
 * the dev harness the shell was built against.
 *
 * It owns the **sign-in conversation** — phone → code → name — and nothing else: the session
 * itself belongs to `SessionManager`, and *"nothing about code validity is decided
 * client-side"* (PRD §5). Every refusal rendered here is one the server returned; the only
 * client-side rejections are the two `@selogate/core` primitives (`normalizePhone`,
 * `normalizeDisplayText`), applied so a malformed value is caught inline rather than by a
 * round trip — and both are the *same* implementation the server validates with, never a
 * second copy of the rule (`contracts.md` §11).
 */

import {
  ROUTES,
  ValidationError,
  buildPath,
  normalizeDisplayText,
  normalizePhone,
  type RoutePath,
} from '@selogate/core'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { AuthScreenPropsContext, SignOutContext } from '../components/auth/context'
import type { AuthScreenProps, AuthStep, AuthTransportError } from '../components/auth/types'
import { useLocale } from '../i18n/I18nProvider'
import { isLocale } from '../i18n/locales'
import { writeLocaleMirror } from '../i18n/localeMirror'
import { AuthStatusContext, type AuthStatus } from '../router/authStatus'
import { StoreTransactionTimeoutError } from '../device-cache/store'
import { MAX_NAME_GRAPHEMES, OTP_RESEND_SECONDS } from './config'
import { SessionContext, type SessionApi } from './context'
import { SessionManager, readVerifiedSignIn, type SessionState } from './session'
import {
  NetworkError,
  SessionUnavailableError,
  fetchTransport,
  field,
  stringField,
  type ApiTransport,
} from './transport'

interface FlowState {
  readonly step: AuthStep
  readonly phone: string
  readonly phoneError?: AuthScreenProps['phoneStep']['error']
  /**
   * The instant the D65/D88 cap releases, straight off the `429`. Memory-only, like every
   * other piece of OTP state here, and cleared with the error it qualifies.
   */
  readonly capRetryAt?: string | undefined
  readonly code: string
  readonly codeError?: AuthScreenProps['codeStep']['error']
  readonly name: string
  readonly nameError?: AuthScreenProps['nameStep']['error']
  /**
   * Seconds left on the resend cooldown, **derived from `resendAvailableAt` rather than
   * decremented**. Nothing writes it but the tick below.
   */
  readonly resendIn: number
  /**
   * The instant the resend cooldown releases, and the number it was issued for.
   *
   * **D248 — the cooldown belongs to the NUMBER, and this pair is what binds it.** It used to
   * be a bare counter tied to nothing: *use a different number* returns to the phone step with
   * `flow.phone` still holding the number that was just used, so re-submitting it sent a second
   * SMS with no wait at all. There is no server-side 30 s to catch that — the platform's only
   * tiers are the D65/D88 cap's — so the countdown was the whole of the control, and it was
   * skippable in two taps.
   *
   * An instant rather than a count for the same reason `capRetryAt` is one: it survives a
   * backgrounded screen and a resumed one, where a decrementing counter drifts and stalls.
   */
  readonly resendAvailableAt?: string | undefined
  readonly resendFor?: string | undefined
  readonly busy: boolean
  /** Cognito's challenge session, opaque and short-lived. Memory-only, like every OTP state. */
  readonly challenge?: string | undefined
  /** The normalized number the code was sent to — what the code step displays. */
  readonly sentTo: string
}

const INITIAL_FLOW: FlowState = {
  step: 'phone',
  phone: '',
  code: '',
  name: '',
  resendIn: 0,
  busy: false,
  sentTo: '',
}

/**
 * The refusal code a thrown error becomes, for all three sign-in handlers.
 *
 * The order is not cosmetic: `SessionUnavailableError` **is a** `NetworkError`, so the
 * narrower test has to come first or the honest copy is never reached.
 *
 * **An unrecognized error becomes `unavailable` rather than being rethrown**, which deviates
 * from this file's original style on purpose. Everywhere else in the app an unhandled
 * rejection is a loud bug that costs the user nothing — here it leaves `busy` set on a screen
 * with no other route out of it, so it is indistinguishable from a frozen app. It is logged
 * instead, and the user keeps a button that works. See `implementation-notes.md` → P6.
 */
function transportErrorOf(error: unknown): AuthTransportError {
  if (error instanceof SessionUnavailableError) return 'unavailable'
  if (error instanceof NetworkError) return 'network'
  console.error('auth-step-failed', error)
  return 'unavailable'
}

/** Idempotent, so no branch and no ordering can leave a submit button permanently disabled. */
const clearBusy = (prev: FlowState): FlowState => (prev.busy ? { ...prev, busy: false } : prev)

/** Install the browser hint and return the complete cleanup used by the React effect. */
export function bindOnlineRecovery(
  manager: Pick<SessionManager, 'recoverConnectivity' | 'cancelConnectivityRecovery'>,
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const recover = () => void manager.recoverConnectivity()
  target.addEventListener('online', recover)
  return () => {
    target.removeEventListener('online', recover)
    manager.cancelConnectivityRecovery()
  }
}

export function AuthProvider({
  children,
  transport = fetchTransport,
}: {
  readonly children: ReactNode
  readonly transport?: ApiTransport
}): JSX.Element {
  const managerRef = useRef<SessionManager>()
  managerRef.current ??= new SessionManager(transport)
  const manager = managerRef.current

  const session = useSyncExternalStore<SessionState>(
    useCallback((listener) => manager.subscribe(listener), [manager]),
    () => manager.state,
  )
  const [flow, setFlow] = useState<FlowState>(INITIAL_FLOW)
  const verificationInFlight = useRef(false)
  const { locale, setLocale } = useLocale()

  // Startup contract steps 1–2 and the session half of 5.
  useEffect(() => {
    void manager.start()
  }, [manager])

  // Browser reachability is only a hint, so it triggers the bounded refresh sequence rather
  // than flipping `online` optimistically. SessionManager coalesces duplicate events.
  useEffect(() => bindOnlineRecovery(manager), [manager])

  // *"Reconcile with the profile after login; a server change wins"* (device-cache.md's
  // cloud-owned settings table). The provider is where the account's locale meets the UI's.
  //
  // D333 — the same effect keeps the blocked screen's mirror of the ACCOUNT's locale, written
  // before the equality test and never from it: a Hebrew account on a UI already in Hebrew calls
  // no `setLocale`, and must still overwrite an English value an earlier account left behind.
  useEffect(() => {
    if (session.locale !== undefined) writeLocaleMirror(session.locale)
    if (session.locale !== undefined && session.locale !== locale) setLocale(session.locale)
    // Depending on `locale` here would fight the user's own toggle: this reconciles when the
    // *account's* value changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.locale, setLocale])

  /*
   * Resend countdown. Memory-only UI state, and **recomputed from the deadline rather than
   * decremented** (D248).
   *
   * Two things follow from that, and both were defects in the version this replaces. The effect
   * depends on the *deadline* only, so one interval runs for the life of the wait — the old one
   * depended on `flow.resendIn` and therefore tore itself down and rebuilt every second. And the
   * remaining time is read off the clock on each tick, so a screen that was backgrounded comes
   * back with the right number instead of however many ticks the browser chose to deliver.
   *
   * It is not scoped to the code step any more: the phone step reads the same countdown now,
   * because that is where the cooldown is enforced.
   */
  useEffect(() => {
    const deadline = flow.resendAvailableAt
    if (deadline === undefined) return undefined
    const releaseAt = Date.parse(deadline)
    if (!Number.isFinite(releaseAt)) return undefined
    let timer = 0
    const apply = () => {
      const left = Math.max(0, Math.ceil((releaseAt - Date.now()) / 1000))
      setFlow((prev) => (prev.resendIn === left ? prev : { ...prev, resendIn: left }))
      // Stop at zero rather than ticking for the life of the session. `AuthProvider` wraps the
      // whole app and the deadline outlives the sign-in that set it, so an interval left running
      // here never stops — the `setFlow` bail keeps it invisible, which is what would let it.
      if (left === 0 && timer !== 0) window.clearInterval(timer)
    }
    apply()
    timer = window.setInterval(apply, 1000)
    return () => window.clearInterval(timer)
  }, [flow.resendAvailableAt])

  // A session that ends while the app is open — a revoke or an LRU eviction detected on some
  // request — returns to a clean phone step rather than to whatever half-filled form was on
  // screen when it happened.
  useEffect(() => {
    if (session.state === 'signed-out') setFlow(INITIAL_FLOW)
  }, [session.state])

  // `RoutePath`, not `string` (D148): the two OTP paths below are the table's entries, and a
  // hand-written literal here would not compile.
  const publicCall = useCallback(
    async (path: RoutePath, body: unknown) => transport({ method: 'POST', path, body }),
    [transport],
  )

  const sendCode = useCallback(
    async (phoneRaw: string, resend: boolean) => {
      let phone: string
      try {
        phone = normalizePhone(phoneRaw, 'IL')
      } catch (error) {
        if (error instanceof ValidationError) {
          setFlow((prev) => ({ ...prev, phoneError: 'invalid-phone' }))
          return
        }
        // Not a rejected number — the normalizer itself failed. Rethrowing left the button
        // enabled and the screen silent, so the user taps forever with nothing to read.
        setFlow((prev) => ({ ...prev, phoneError: transportErrorOf(error) }))
        return
      }

      /*
       * **D248 — the resend cooldown, enforced against the number rather than the screen.**
       *
       * This is the bypass it closes: *use a different number* returns to the phone step
       * without clearing `flow.phone`, so the field comes back holding the number that was
       * just used and is immediately submittable. Nothing server-side would have refused it —
       * the platform has no 30 s tier, only the D65/D88 cap — so the whole of the control was
       * a countdown on a screen the user had just left.
       *
       * A genuinely **different** number is let through, and that is correct rather than a
       * hole: the cap is keyed `OTP#<phone>` and is independent per number by design, so a
       * cooldown that followed the caller instead of the number would be a per-device limiter
       * this product never decided to have.
       *
       * On a *resend* the guard returns silently. The code step renders the live countdown in
       * place of its Resend control, so the affordance is already gone and there is nothing to
       * say; an error there would be a second sentence about a wait already on screen.
       */
      const releaseAt =
        phone === flow.resendFor && flow.resendAvailableAt !== undefined
          ? Date.parse(flow.resendAvailableAt)
          : Number.NaN
      if (Number.isFinite(releaseAt) && Date.now() < releaseAt) {
        if (!resend) setFlow((prev) => ({ ...prev, phoneError: 'resend-blocked' }))
        return
      }

      setFlow((prev) => ({
        ...prev,
        busy: true,
        phoneError: undefined,
        codeError: undefined,
        capRetryAt: undefined,
      }))
      try {
        const response = await publicCall(ROUTES.otpStart.template, { phone, locale })
        if (response.status === 429) {
          /*
           * **The wait is on the response and this is where it stopped being thrown away.**
           *
           * P2 computes the instant the cap releases and has always sent it; this handler
           * mapped every `429` to one timeless line and bounced the user back to the phone
           * step. The cap's two tiers are 3 per 15 minutes and 5 per rolling 24 hours, so the
           * line the user saw covered a wait of a quarter of an hour and a wait until the next
           * morning with the same four words — and on 1 Sep 2026 a pilot user sat on the second
           * one for ~22 hours with nothing on screen saying so.
           *
           * `retryAt` is read rather than `retryAfterSeconds` because what is rendered is a
           * wall-clock time: an instant survives a backgrounded screen, and a duration would
           * have to be counted down. **It is optional on purpose** — the per-IP ceiling answers
           * `429` on this same route with no cap time in it, and `AuthError` falls back to the
           * timeless string rather than inventing one.
           */
          const capRetryAt = stringField(response.body, 'retryAt')
          setFlow((prev) => ({
            ...prev,
            step: 'phone',
            phoneError: 'too-many-codes',
            ...(capRetryAt === undefined ? {} : { capRetryAt }),
          }))
          return
        }
        if (response.status !== 200) {
          /*
           * **`503` is the server saying the SMS was not sent (P2's `otp-send-failed`, D316),
           * and it is the one non-`429` refusal this screen can name honestly.**
           *
           * It reaches the resident as `unavailable` — *service is temporarily unavailable —
           * try again* — rather than `server`'s *something went wrong*, because that is what
           * happened: the request was fine, the provider refused the message, and the wait is
           * not the resident's to serve out. Every other non-`200` stays `server`.
           */
          const code = response.status === 503 ? ('unavailable' as const) : ('server' as const)
          setFlow((prev) => ({
            ...prev,
            ...(resend ? { codeError: code } : { step: 'phone' as const, phoneError: code }),
          }))
          return
        }
        const challenge = stringField(response.body, 'session')
        setFlow((prev) => ({
          ...prev,
          step: 'code',
          sentTo: phone,
          code: '',
          codeError: undefined,
          resendIn: OTP_RESEND_SECONDS,
          resendAvailableAt: new Date(Date.now() + OTP_RESEND_SECONDS * 1000).toISOString(),
          resendFor: phone,
          ...(challenge === undefined ? {} : { challenge }),
        }))
      } catch (error) {
        const code = transportErrorOf(error)
        setFlow((prev) => ({ ...prev, ...(resend ? { codeError: code } : { phoneError: code }) }))
      } finally {
        // Every exit clears `busy` in one place, including the ones that return early.
        setFlow(clearBusy)
      }
    },
    // The two cooldown fields are read above, so they belong here. This re-creates `sendCode`
    // once per send rather than per render — unlike `session.request`, whose identity changes
    // on every response and must never reach a dependency array (AGENTS.md §4.9).
    [flow.resendAvailableAt, flow.resendFor, locale, publicCall],
  )

  const verifyCode = useCallback(
    async (code: string) => {
      if (verificationInFlight.current) return
      verificationInFlight.current = true
      setFlow((prev) => ({ ...prev, busy: true, codeError: undefined }))
      try {
        const response = await publicCall(ROUTES.otpVerify.template, {
          phone: flow.sentTo,
          session: flow.challenge ?? '',
          code,
          locale,
          // §13 "reuses its session record rather than consuming an additional slot" — offered only when this device remembers a session it lost the keys to.
          ...(manager.resumeSessionId() === undefined ? {} : { resumeSessionId: manager.resumeSessionId() }),
        })

        if (response.status === 401) {
          setFlow((prev) => ({ ...prev, code: '', codeError: 'wrong-code' }))
          return
        }
        if (response.status !== 200) {
          setFlow((prev) => ({ ...prev, codeError: 'server' }))
          return
        }
        // P2's "attempts remain against the same code" shape: a new challenge, not a session.
        if (field(response.body, 'retry') === true) {
          const challenge = stringField(response.body, 'session')
          setFlow((prev) => ({
            ...prev,
            code: '',
            codeError: 'wrong-code',
            ...(challenge === undefined ? {} : { challenge }),
          }))
          return
        }

        const verified = readVerifiedSignIn(response.body)
        if (verified === undefined) {
          setFlow((prev) => ({ ...prev, codeError: 'server' }))
          return
        }
        try {
          await manager.adoptSignIn(verified.signIn, verified.accessToken, verified.expiresIn)
        } catch (adoptError) {
          // R1-7(f): `adoptSignIn`'s `writeCredentials` stays fail-closed (D128) — this is that
          // throw, reaching here as a raw, untyped exception because `fetchTransport` had nothing
          // to do with it (every path above this line either resolved or threw a typed
          // `NetworkError`/`SessionUnavailableError`, caught below). The server verified the code
          // and answered; the device's own local store is what refused the write, so
          // `serviceUnavailable`'s "try again" is a lie — retrying repeats the identical write to
          // the identical broken store. Named as itself instead, isolated to this one `catch` so
          // sendCode/submitName's unrelated unrecognized-error fallback (`serviceUnavailable`)
          // is untouched (`AuthProvider.test.tsx`'s "reports a service failure" case still holds).
          //
          // **D304 splits the transient case out, and it is a correctness requirement rather than
          // a copy improvement.** `run()`'s deadline throws a distinguishable
          // `StoreTransactionTimeoutError`; a timeout is genuinely transient, so `serviceUnavailable`'s
          // "try again" is honest for it and `storageUnavailable`'s "reinstall the app" is not. The
          // deeper reason it cannot be skipped: the same commit narrows
          // `STORE_TRANSACTION_TIMEOUT_MS` from 10 s to 2 s, and `storeRecovery.ts` refuses to
          // destroy a database on a timeout — so this branch is what stands between a false timeout
          // and a healthy store being rebuilt. A **class**, not a matched message: two call sites
          // now branch on it, and message-matching across a module boundary stops working silently
          // the day someone rewords a string.
          console.error('credential-store-write-failed', adoptError)
          const transient = adoptError instanceof StoreTransactionTimeoutError
          setFlow((prev) => ({ ...prev, codeError: transient ? 'unavailable' : 'storage' }))
          return
        }
        // D42 — the name step is shown only when the account has no self-name yet. The router's
        // `requireAuth` guard holds the viewer on `auth` until it is captured.
        setFlow((prev) => ({
          ...prev,
          code: '',
          challenge: undefined,
          step: verified.signIn.profile.name === undefined ? 'name' : 'phone',
        }))
      } catch (error) {
        setFlow((prev) => ({ ...prev, codeError: transportErrorOf(error) }))
      } finally {
        verificationInFlight.current = false
        setFlow(clearBusy)
      }
    },
    [flow.challenge, flow.sentTo, locale, manager, publicCall],
  )

  const submitName = useCallback(async () => {
    let name: string
    try {
      // **D57 inline, with P0's primitive** (§13 "Display-text validation holds the line without over-blocking (D57)"): a bidi override, isolate, or C0
      // control is refused here and never sent; permitted emoji and mixed scripts pass
      // through NFC-normalized and unchanged.
      name = normalizeDisplayText(flow.name, MAX_NAME_GRAPHEMES, 'name')
    } catch (error) {
      if (error instanceof ValidationError) {
        setFlow((prev) => ({
          ...prev,
          nameError: prev.name.trim().length === 0 ? 'name-required' : 'invalid-name',
        }))
        return
      }
      // Same as `sendCode`'s: a normalizer that throws for a reason other than a refused
      // value is a dead end on a gate the user cannot leave, whatever `busy` says.
      setFlow((prev) => ({ ...prev, nameError: transportErrorOf(error) }))
      return
    }

    setFlow((prev) => ({ ...prev, busy: true, nameError: undefined }))
    try {
      const response = await manager.request({
        method: ROUTES.profileUpdate.method,
        path: buildPath(ROUTES.profileUpdate),
        body: { name, locale },
      })
      if (response.status === 422) {
        setFlow((prev) => ({ ...prev, nameError: 'invalid-name' }))
        return
      }
      if (response.status !== 200) {
        setFlow((prev) => ({ ...prev, nameError: 'server' }))
        return
      }
      const userId = stringField(response.body, 'userId')
      const stored = stringField(response.body, 'name')
      const rawLocale = stringField(response.body, 'locale')
      if (userId !== undefined) {
        await manager.rememberProfile({
          userId,
          ...(stored === undefined ? {} : { name: stored }),
          // The fifth boundary that narrows an unvalidated locale string, and the last one that
          // was still spelled by hand. See `i18n/locales.ts`'s `isLocale` for why they all share
          // one guard now: the failure mode of missing one is a silent fall back to Hebrew.
          locale: isLocale(rawLocale) ? rawLocale : 'he',
        })
      }
      setFlow((prev) => ({ ...prev, step: 'phone', name: '' }))
    } catch (error) {
      setFlow((prev) => ({ ...prev, nameError: transportErrorOf(error) }))
    } finally {
      // **The freeze this whole change exists for.** `NameStep`'s button is disabled while
      // `submitting`, `requireAuth` holds the viewer here until a name is captured, and no
      // other path clears the flag — so one unanticipated throw was a permanently dead app.
      setFlow(clearBusy)
    }
  }, [flow.name, locale, manager])

  const authStatus = useMemo<AuthStatus>(() => {
    if (session.state === 'loading') return { state: 'loading' }
    // Mapped explicitly, because the fall-through below would read it as signed in with no name —
    // which holds the viewer on `auth` for the name step, the OTP screen by another door.
    if (session.state === 'restoring') return { state: 'restoring' }
    if (session.state === 'signed-out') return { state: 'signed-out' }
    return { state: 'signed-in', pendingName: session.name === undefined }
  }, [session.state, session.name])

  const authScreenProps = useMemo<AuthScreenProps>(
    () => ({
      // A signed-in account still missing its name is on the name step regardless of how it
      // got here — including a reload mid-capture, which the guard keeps on `auth`.
      step: authStatus.state === 'signed-in' && authStatus.pendingName ? 'name' : flow.step,
      phoneStep: {
        phone: flow.phone,
        onChangePhone: (value) =>
          setFlow((prev) => ({ ...prev, phone: value, phoneError: undefined, capRetryAt: undefined })),
        error: flow.phoneError,
        ...(flow.capRetryAt === undefined ? {} : { retryAt: flow.capRetryAt }),
        resendInSeconds: flow.resendIn,
        submitting: flow.busy,
        onSubmit: () => void sendCode(flow.phone, false),
        // A state of this screen, not a new one (D255): present only when the boot read ran out of
        // retries, so the resident is told the phone's storage could not be read rather than
        // shown a bare OTP screen over a session that may still be on it.
        ...(session.credentialReadFailed === true
          ? { onRetryRestore: () => void manager.retryRestore() }
          : {}),
      },
      codeStep: {
        phone: flow.sentTo,
        code: flow.code,
        onChangeCode: (value) => setFlow((prev) => ({ ...prev, code: value, codeError: undefined })),
        error: flow.codeError,
        verifying: flow.busy,
        resendInSeconds: flow.resendIn,
        onResend: () => void sendCode(flow.sentTo, true),
        onChangeNumber: () => setFlow((prev) => ({ ...prev, step: 'phone', code: '', codeError: undefined })),
        onVerify: (code) => void verifyCode(code),
      },
      nameStep: {
        name: flow.name,
        onChangeName: (value) => setFlow((prev) => ({ ...prev, name: value, nameError: undefined })),
        error: flow.nameError,
        submitting: flow.busy,
        onSubmit: () => void submitName(),
        onSignOut: () => void manager.signOut(),
      },
    }),
    [authStatus, flow, manager, sendCode, submitName, verifyCode, session.credentialReadFailed],
  )

  const signOut = useCallback(() => void manager.signOut(), [manager])

  const sessionApi = useMemo<SessionApi>(
    () => ({
      ...session,
      request: (request) => manager.request(request),
      endSession: (reason) => manager.endSession(reason),
      rememberProfile: (profile) => manager.rememberProfile(profile),
      // D264 — the second caller of a recovery that had only the browser's `online` event, which
      // the Android WebView does not always fire. `bindOnlineRecovery` above keeps the first.
      recoverConnectivity: () => manager.recoverConnectivity(),
    }),
    [manager, session],
  )

  return (
    <SessionContext.Provider value={sessionApi}>
      <AuthStatusContext.Provider value={authStatus}>
        <SignOutContext.Provider value={signOut}>
          <AuthScreenPropsContext.Provider value={authScreenProps}>
            {children}
          </AuthScreenPropsContext.Provider>
        </SignOutContext.Provider>
      </AuthStatusContext.Provider>
    </SessionContext.Provider>
  )
}
